import React, { useEffect, useState } from 'react'
import {
  BoldItalicUnderlineToggles,
  listsPlugin,
  ListsToggle,
  MDXEditor,
  MDXEditorMethods,
  toolbarPlugin,
} from '@mdxeditor/editor'
import { Remark } from 'react-remark'

import { useAuth } from '../hooks/useAuth'
import useTemplateFetcher from '../hooks/useTemplateFetcher'
import { TempConsignment } from '../interfaces/TemplateDto'
import useTemplatePoster from '../hooks/useTemplatePoster'
import { DEFAULT_TITLE_CONFIG } from '../utils/documentTitleUtils'

import { RadioGroupEx, RadioItem } from './RadioGroup'
import { ButtonAction } from './Buttons'
import { Info } from './MessageInfo'
import CheckboxField from './CheckBoxField'
import TitleEditor from './TitleEditor'

const predetFirstCollection: RadioItem[] = [
  { label: 'Primer párrafo (Usar Prederminado)' },
  { label: 'Primer párrafo (Usar Personalizado)' },
]
const predetSecondCollection: RadioItem[] = [
  { label: 'Segundo párrafo (Usar Prederminado)' },
  { label: 'Segundo párrafo (Usar Personalizado)' },
]
const predetFooterCollection: RadioItem[] = [
  { label: 'Pie (Usar Prederminado)' },
  { label: 'Pie (Usar Personalizado)' },
]
const TemplateConsignment: React.FC<{ setClickedIndex: React.Dispatch<React.SetStateAction<number>> }> = ({
  setClickedIndex,
}) => {
  const refPrimer = React.useRef<MDXEditorMethods>(null)
  const refSegundo = React.useRef<MDXEditorMethods>(null)
  const refPie = React.useRef<MDXEditorMethods>(null)

  const { userData } = useAuth()
  const [primerIndex, setPrimerIndex] = useState<number>(0)
  const [segundoIndex, setSegundoIndex] = useState<number>(0)
  const [pieIndex, setPieIndex] = useState<number>(0)
  const [infoMessage, setInfoMessage] = useState<string>('')
  const [carmap, setCarmap] = useState<boolean>(false)
  const [titleData, setTitleData] = useState(DEFAULT_TITLE_CONFIG)
  const defaultTitle = 'Contrato de Consignación'
  const { postTemplateCon, errorPostTemplate } = useTemplatePoster()
  const { errorTemplate, fetchTemplateConFull } = useTemplateFetcher()
  const [template, setTemplate] = useState<{ default: TempConsignment; custom: TempConsignment } | null>(null)

  const organizationId = userData?.organization?._id
  const buttonDisabled = false
  useEffect(() => {
    const fetchDataAsync = async () => {
      if (!organizationId) return
      const data = await fetchTemplateConFull(organizationId)

      if (data) {
        setTemplate(data)
        setCarmap(data.custom.carmap ?? false)
        refPrimer.current?.setMarkdown(data.custom.body.first.value ?? '')
        refSegundo.current?.setMarkdown(data.custom.body.second.value ?? '')
        refPie.current?.setMarkdown(data.custom.footer.value ?? '')
        setPrimerIndex(data.custom.body.first.type === '.' ? 1 : 0)
        setSegundoIndex(data.custom.body.second.type === '.' ? 1 : 0)
        setPieIndex(data.custom.footer.type === '.' ? 1 : 0)

        // Set title data with fallback
        const customTitle = data.custom.title ?? { ...DEFAULT_TITLE_CONFIG }
        setTitleData(customTitle)
      }
    }
    fetchDataAsync()
  }, [organizationId])

  const firstDefault = template?.default.body.first.value ?? ''
  const secondDefault = template?.default.body.second.value ?? ''
  const footerDefault = template?.default.footer.value ?? ''

  const handleTitleChange = (newTitle: any) => {
    setTitleData(newTitle)
  }

  const handleStoreClick = async () => {
    const firstMark = refPrimer.current?.getMarkdown()
    const secondMark = refSegundo.current?.getMarkdown()
    const footMark = refPie.current?.getMarkdown()

    const template: TempConsignment = {
      title: titleData,
      body: {
        first: {
          type: primerIndex === 0 ? '' : '.',
          value: firstMark,
        },
        second: {
          type: segundoIndex === 0 ? '' : '.',
          value: secondMark,
        },
      },
      carmap: carmap,
      footer: {
        type: pieIndex === 0 ? '' : '.',
        value: footMark,
      },
    }

    console.log('Saving consignment template:', template)
    console.log('Title data being sent:', titleData)

    const res = await postTemplateCon(template)

    if (res) {
      console.log('Template saved successfully')
      setClickedIndex(-1)
    } else {
      console.error('Failed to save template')
    }
  }
  const handleCancelClick = async () => {
    setClickedIndex(-1)
  }
  const handleCheckChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { checked } = e.target

    setCarmap(checked)
  }

  return (
    <>
      <TitleEditor
        titleData={titleData}
        onTitleChange={handleTitleChange}
        defaultTitle={defaultTitle}
        documentType="consignment"
      />

      <div className="border">
        {/**    <div className="text-2xl text-center mb-4">Primer párrafo</div>*/}
        <div className="grid grid-cols-2 gap-2 ml-2 mb-2">
          <RadioGroupEx
            collection={predetFirstCollection}
            name={'primer'}
            checked={primerIndex}
            callback={(idx) => setPrimerIndex(idx)}
            className="flex items-center space-x-2 mr-10 mb-1"
            labelClassName="font-bold"
          />

          <div className="border">
            <Remark>{firstDefault}</Remark>
          </div>
          <MDXEditor
            ref={refPrimer}
            markdown=""
            plugins={[
              listsPlugin(),
              toolbarPlugin({
                toolbarClassName: 'my-classname',
                toolbarContents: () => (
                  <>
                    <BoldItalicUnderlineToggles />
                    <ListsToggle />
                  </>
                ),
              }),
            ]}
          />
        </div>
      </div>
      <div className="border mt-4">
        <div className="grid grid-cols-2 gap-2 ml-2 mb-2">
          <RadioGroupEx
            collection={predetSecondCollection}
            name={'segundo'}
            checked={segundoIndex}
            callback={(idx) => setSegundoIndex(idx)}
            className="flex items-center space-x-2 mr-10 mb-1"
            labelClassName="font-bold"
          />
          <div className="border">
            <Remark>{secondDefault}</Remark>
          </div>
          <MDXEditor
            ref={refSegundo}
            markdown=""
            plugins={[
              listsPlugin(),
              toolbarPlugin({
                toolbarClassName: 'my-classname',
                toolbarContents: () => (
                  <>
                    <BoldItalicUnderlineToggles />
                    <ListsToggle />
                  </>
                ),
              }),
            ]}
          />
        </div>
      </div>

      <div className="border mt-4">
        <div className="grid grid-cols-2 gap-2 ml-2 mb-2">
          <RadioGroupEx
            collection={predetFooterCollection}
            name={'pie'}
            checked={pieIndex}
            callback={(idx) => setPieIndex(idx)}
            className="flex items-center space-x-2 mr-10 mb-1"
            labelClassName="font-bold"
          />
          <div className="border">
            <Remark>{footerDefault}</Remark>
          </div>
          <MDXEditor
            ref={refPie}
            markdown=""
            plugins={[
              listsPlugin(),
              toolbarPlugin({
                toolbarClassName: 'my-classname',
                toolbarContents: () => (
                  <>
                    <BoldItalicUnderlineToggles />
                    <ListsToggle />
                  </>
                ),
              }),
            ]}
          />
        </div>
      </div>
      <div className="border mt-4">
        <CheckboxField
          label="Mostrar anotaciones del vehículo"
          name="carmap"
          value=""
          checked={carmap}
          handleInputChange={handleCheckChange}
        />
      </div>
      <div className="flex justify-center mt-4">
        <ButtonAction label="Grabar" handleButtonClick={handleStoreClick} disabled={buttonDisabled} />
        <div className="ml-4">
          <ButtonAction label="Cerrar" handleButtonClick={handleCancelClick} />
        </div>
      </div>
      <div className="flex justify-center mt-4">
        <Info message={infoMessage} trigger={Date.now()} onTimeout={() => setInfoMessage('')} noCenter={true} />
      </div>
      <div>{errorTemplate?.message}</div>
      <div>{errorPostTemplate?.message}</div>
    </>
  )
}
export default TemplateConsignment
